/**
 * Per-topic item analysis: concept mastery across the class, then every question
 * with its difficulty (% correct), average time, and how many wrong answers chose
 * the misconception distractor.
 */

import { ArrowLeft, Clock, AlertTriangle } from 'lucide-react';
import type { ClassRoster } from './mockData';
import { topicStats, conceptStats, itemStats, conceptsOfTopic, misconceptionLabel } from './mockData';
import { Panel, StackedMasteryBar, ProgressBar, MasteryLegend } from './charts';

export function ItemAnalysis({ roster, topicId, onBack }: { roster: ClassRoster; topicId: string; onBack: () => void }) {
  const t = roster.topics.find((x) => x.id === topicId)!;
  const ts = topicStats(roster, topicId);
  const items = itemStats(roster, topicId);
  const attempted = ts.mastered + ts.developing + ts.emerging;

  return (
    <div>
      <button onClick={onBack} className="mb-5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#6D28D9] transition-colors hover:text-[#7C3AED] dark:text-[#B69BF5]">
        <ArrowLeft className="h-4 w-4" /> Back to class
      </button>

      <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-black/10 bg-white p-6 dark:border-white/10 dark:bg-white/[0.04]">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#8A8593]">{roster.subject} · {roster.grade}</div>
          <h1 className="mt-1 font-serif text-[28px] font-medium tracking-tight">{t.title}</h1>
          <p className="mt-1 text-[13px] text-[#8A8593]">{attempted} of {roster.learners.length} learners attempted · {t.totalQuestions} questions</p>
        </div>
        <div className="text-right">
          <div className="font-serif text-[34px] font-medium leading-none tabular-nums" style={{ color: ts.avgScorePercent >= 70 ? '#1F6B5F' : ts.avgScorePercent >= 55 ? '#9A7320' : '#B23B4E' }}>{ts.avgScorePercent}%</div>
          <div className="mt-1 text-[10.5px] font-semibold uppercase tracking-wide text-[#8A8593]">class average</div>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Panel title="Concept mastery" sub="How the class splits across each concept in this topic" right={<MasteryLegend />}>
          <div className="space-y-4">
            {conceptsOfTopic(topicId).map((c) => {
              const cs = conceptStats(roster, c.tag);
              return (
                <div key={c.tag}>
                  <div className="mb-1.5 flex items-center justify-between text-[13px]">
                    <span className="flex items-center gap-1.5 font-medium">
                      {c.label}
                      {cs.misconceptionHolders > 0 && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#B23B4E]">
                          <AlertTriangle className="h-3 w-3" />{cs.misconceptionHolders}
                        </span>
                      )}
                    </span>
                    <span className="tabular-nums text-[#8A8593]">{cs.avgScorePercent}% avg</span>
                  </div>
                  <StackedMasteryBar mastered={cs.mastered} developing={cs.developing} emerging={cs.emerging} notAttempted={cs.notAttempted} />
                </div>
              );
            })}
          </div>
        </Panel>

        <Panel title="Question breakdown" sub="Difficulty and time per item; flagged where wrong answers cluster on the misconception">
          <div className="space-y-3">
            {items.map((it, i) => (
              <div key={`${it.conceptTag}-${it.questionId}-${i}`} className="rounded-xl border border-black/[0.07] p-3 dark:border-white/[0.08]">
                <div className="flex items-center justify-between text-[12.5px]">
                  <span className="font-medium">{it.conceptLabel} <span className="text-[#8A8593]">· {it.questionId}</span></span>
                  <span className="inline-flex items-center gap-2.5 text-[#8A8593]">
                    <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" />{(it.avgLatencyMs / 1000).toFixed(1)}s</span>
                    <b className="tabular-nums" style={{ color: it.correctPercent >= 70 ? '#1F6B5F' : it.correctPercent >= 50 ? '#9A7320' : '#B23B4E' }}>{it.correctPercent}%</b>
                  </span>
                </div>
                <div className="mt-1.5">
                  <ProgressBar percent={it.correctPercent} color={it.correctPercent >= 70 ? '#2F8F80' : it.correctPercent >= 50 ? '#C99A3B' : '#CB5B6B'} height={6} />
                </div>
                {it.misconceptionTag && it.misconceptionPercent >= 40 && (
                  <div className="mt-2 flex items-start gap-1.5 text-[11.5px] leading-[1.45] text-[#B23B4E]">
                    <AlertTriangle className="mt-[1px] h-3 w-3 shrink-0" />
                    <span>{it.misconceptionPercent}% of wrong answers chose: {misconceptionLabel(it.misconceptionTag).replace(/\.$/, '').toLowerCase()}.</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}
